<template>
  <ul class="network-icons" :style="{ '--network-icon-size': `${props.size}px` }">
    <li v-for="entry in entries" :key="entry.channel" class="network-icons__item">
      <a
        v-if="entry.permalink"
        class="network-icons__link"
        :href="entry.permalink"
        target="_blank"
        rel="noopener noreferrer"
        :title="`View on ${getSocialChannelLabel(entry.channel)}`"
      >
        <img class="network-icons__logo" :src="logoSrc(entry.channel)" :alt="getSocialChannelLabel(entry.channel)" />
      </a>
      <img v-else class="network-icons__logo" :src="logoSrc(entry.channel)" :alt="getSocialChannelLabel(entry.channel)" :title="getSocialChannelLabel(entry.channel)" />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed } from "vue";

// Types
import { getSocialChannelLabel, sortSocialChannelsForDisplay } from "@contracts/socialNetworks";
import type { SocialReportChannel } from "@contracts/socialReports";

/** A network, optionally linked to the post's permalink on that network. */
export interface NetworkIconEntry {
  channel: SocialReportChannel;
  permalink?: string | null;
}

const props = withDefaults(
  defineProps<{
    /** Either plain channels or channel + permalink pairs. */
    channels: Array<SocialReportChannel | NetworkIconEntry>;
    size?: number;
  }>(),
  { size: 20 }
);

const entries = computed<NetworkIconEntry[]>(() => {
  const byChannel = new Map<SocialReportChannel, string | null>();
  for (const item of props.channels) {
    const entry: NetworkIconEntry = typeof item === "string" ? { channel: item } : item;
    if (!entry?.channel) continue;
    // Keep the first permalink we see for a network.
    if (!byChannel.has(entry.channel) || (!byChannel.get(entry.channel) && entry.permalink)) byChannel.set(entry.channel, entry.permalink ?? null);
  }
  return sortSocialChannelsForDisplay([...byChannel.keys()]).map(channel => ({ channel, permalink: byChannel.get(channel) ?? null }));
});

// Same assets the post previews use (frontend/public/images/social-logo-*.svg).
function logoSrc(channel: SocialReportChannel): string {
  return `/images/social-logo-${channel.toLowerCase()}.svg`;
}
</script>

<style scoped lang="scss">
// Network icons ==============================================================
.network-icons {
  gap: 6px;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  list-style: none;

  &__item {
    width: var(--network-icon-size, 20px);
    height: var(--network-icon-size, 20px);
    display: flex;
    align-items: center;
  }

  &__link {
    width: 100%;
    display: flex;
    align-items: center;
    transition: opacity 0.1s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  &__logo {
    width: 100%;
    display: block;
  }
}
</style>
